import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../../utils/formatters';
import {
  proponiDescrizione,
  ComponenteDescrizione,
} from '../../../../utils/configurazioneDescrizione';

// ── Tipi ──────────────────────────────────────────────────────────────────
type SorgenteTipo = 'MODELLO' | 'PRECONFIGURATO' | 'LIBERA';

interface ArticoloLite {
  id: number;
  codice: string;
  descrizioneBase: string;
  tipologia: string;
  umVendita?: { simbolo?: string | null } | null;
  prezzoListinoVendita?: string | null;
}

// Componenti esistenti sulla riga (mappati dal GET /api/ordini/:id)
interface ComponenteRigaEsistente {
  id: number;
  ordinePos: number;
  tipologia: string;
  articoloId: number | null;
  articolo?: { id: number; codice: string; descrizioneBase: string; tipologia?: string } | null;
  descrizione: string;
  umVendita: string | null;
  quantita: string | null;
  sfrido: string | null;
  costoUnitario: string | null;
  prezzoVenditaUnitario: string | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
}

interface RigaProp {
  id: number;
  articolo?: { id: number; codice: string; descrizioneBase: string } | null;
  codicePrecopy: string | null;
  descrizione: string;
  descrizioneEstesa: string | null;
  umVendita: string | null;
  quantita: string | null;
  prezzoUnitario: string | null;
  importoNetto: string | null;
  configurataAt: string | null;
  modelloConfigurazioneId: number | null;
  articoloPrecConfiguratoId: number | null;
  componenti: ComponenteRigaEsistente[];
}

// Lookup: modello in lista
interface ModelloListaItem {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

// Lookup: modello dettaglio
interface SlotModello {
  id: number;
  ordine: number;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  categoriaNome: string;
  quantita: number | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
}
interface ModelloDettaglio {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  descrizioneTemplate: string;
  componenti: SlotModello[];
}

// Lookup: preconfigurato in lista
interface PreconfListaItem {
  id: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  articoloPadreCodice: string;
  articoloPadreNome: string;
  umVenditaSimbolo: string;
  nComponenti: number;
}

// Lookup: preconfigurato dettaglio
interface ComponentePreconf {
  id: number;
  ordine: number;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  categoriaNome: string;
  articoloId: number | null;
  articoloCodice: string;
  articoloDescrizione: string;
  articoloUmVenditaSimbolo: string;
  quantita: number;
  sfrido: string | number | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
}
interface PreconfDettaglio {
  id: number;
  societaId: number;
  codice: string;
  nome: string;
  articoloPadreId: number;
  componenti: ComponentePreconf[];
}

// Forma figlia in editing
interface FigliaForm {
  key: string;
  articolo: ArticoloLite | null;
  quantita: string;
  prezzoListino: string;
  scontoAcquisto: string;
  scontoVendita: string;
  prezzoVenditaUnitario: string;
  costoUnitario: string;
  sfrido: string;
  isPrincipale: boolean;
  isInDescrizione: boolean;
  // Tracciamento sorgente: l'id slot/distinta di provenienza (null se libero)
  componenteModelloId: number | null;
  componentePrecConfiguratoId: number | null;
  // Etichetta dello slot/distinta per UX (categoria/tipologia)
  slotEtichetta: string | null;
  // Ordine slot del modello (per compilare il template descrizione)
  slotOrdine: number | null;
  // Picker articolo
  search: string;
  searchOpen: boolean;
}

interface ErroreQuadratura {
  forfait?: number;
  sommaFigli?: number;
  differenza?: number;
}

interface Props {
  ordineId: number;
  societaId: number;
  riga: RigaProp;
  onClose: () => void;
  onSaved: () => void;
}

let _keySeq = 0;
function nextKey(): string {
  _keySeq += 1;
  return `c-${Date.now()}-${_keySeq}`;
}

function toCents(n: number): number {
  return Math.round(n * 100);
}

function emptyFiglia(): FigliaForm {
  return {
    key: nextKey(),
    articolo: null,
    quantita: '',
    prezzoListino: '',
    scontoAcquisto: '',
    scontoVendita: '',
    prezzoVenditaUnitario: '',
    costoUnitario: '',
    sfrido: '',
    isPrincipale: false,
    isInDescrizione: true,
    componenteModelloId: null,
    componentePrecConfiguratoId: null,
    slotEtichetta: null,
    slotOrdine: null,
    search: '',
    searchOpen: false,
  };
}

// ── Componente principale ─────────────────────────────────────────────────
const ModalConfigurazioneRigaOrdine: React.FC<Props> = ({
  ordineId,
  societaId,
  riga,
  onClose,
  onSaved,
}) => {
  const forfait = useMemo(() => Number(riga.importoNetto ?? 0), [riga.importoNetto]);
  const giaConfigurata = !!riga.configurataAt;

  // Stato sorgente
  const sorgenteIniziale: SorgenteTipo = riga.modelloConfigurazioneId
    ? 'MODELLO'
    : riga.articoloPrecConfiguratoId
      ? 'PRECONFIGURATO'
      : 'LIBERA';
  const [sorgenteTipo, setSorgenteTipo] = useState<SorgenteTipo>(sorgenteIniziale);
  const [modelloIdSel, setModelloIdSel] = useState<number | null>(riga.modelloConfigurazioneId);
  const [preconfIdSel, setPreconfIdSel] = useState<number | null>(riga.articoloPrecConfiguratoId);
  const [modelloDett, setModelloDett] = useState<ModelloDettaglio | null>(null);
  const [preconfDett, setPreconfDett] = useState<PreconfDettaglio | null>(null);
  const [modelliLista, setModelliLista] = useState<ModelloListaItem[]>([]);
  const [preconfLista, setPreconfLista] = useState<PreconfListaItem[]>([]);
  const [loadingLookup, setLoadingLookup] = useState(false);

  // Figli
  const [figli, setFigli] = useState<FigliaForm[]>(() => {
    if (giaConfigurata && riga.componenti.length > 0) {
      return riga.componenti.map((c) => ({
        key: nextKey(),
        articolo: c.articolo
          ? {
              id: c.articolo.id,
              codice: c.articolo.codice,
              descrizioneBase: c.articolo.descrizioneBase,
              tipologia: c.articolo.tipologia ?? c.tipologia,
              umVendita: c.umVendita ? { simbolo: c.umVendita } : null,
            }
          : null,
        quantita: c.quantita ?? '',
        prezzoListino: '',
        scontoAcquisto: '',
        scontoVendita: '',
        prezzoVenditaUnitario: c.prezzoVenditaUnitario ?? '',
        costoUnitario: c.costoUnitario ?? '',
        sfrido: c.sfrido ?? '',
        isPrincipale: c.isPrincipale,
        isInDescrizione: c.isInDescrizione,
        componenteModelloId: null,
        componentePrecConfiguratoId: null,
        slotEtichetta: null,
        slotOrdine: null,
        search: '',
        searchOpen: false,
      }));
    }
    return [];
  });

  // Descrizione
  const [descrizioneEstesa, setDescrizioneEstesa] = useState<string>(riga.descrizioneEstesa ?? '');

  const [salvando, setSalvando] = useState(false);
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [dettaglioErrore, setDettaglioErrore] = useState<ErroreQuadratura | null>(null);

  // ── Carica liste lookup per sorgente attiva ──────────────────────────────
  useEffect(() => {
    let alive = true;
    const carica = async () => {
      setLoadingLookup(true);
      try {
        if (sorgenteTipo === 'MODELLO') {
          // @api GET /api/modelli-configurazione?societaId -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","articoloPadreId":"number","articoloPadreCodice":"string","articoloPadreNome":"string","umVenditaSimbolo":"string","nComponenti":"number"}]}
          const r = await apiFetch<{ success: boolean; data: ModelloListaItem[] }>(
            `/api/modelli-configurazione?societaId=${societaId}`,
          );
          if (alive) setModelliLista(Array.isArray(r.data) ? r.data : []);
        } else if (sorgenteTipo === 'PRECONFIGURATO') {
          // @api GET /api/articoli-preconfigurati?societaId -> {"success":true,"data":[{"id":"number","codice":"string","nome":"string","articoloPadreId":"number","articoloPadreCodice":"string","articoloPadreNome":"string","umVenditaSimbolo":"string","nComponenti":"number"}]}
          const r = await apiFetch<{ success: boolean; data: PreconfListaItem[] }>(
            `/api/articoli-preconfigurati?societaId=${societaId}`,
          );
          if (alive) setPreconfLista(Array.isArray(r.data) ? r.data : []);
        }
      } catch {
        // silenzioso: la lista resta vuota; l'utente vedrà "Nessun risultato"
      } finally {
        if (alive) setLoadingLookup(false);
      }
    };
    carica();
    return () => {
      alive = false;
    };
  }, [sorgenteTipo, societaId]);

  // ── Carica dettaglio modello quando cambia modelloIdSel ──────────────────
  useEffect(() => {
    let alive = true;
    if (sorgenteTipo !== 'MODELLO' || !modelloIdSel) {
      setModelloDett(null);
      return;
    }
    const carica = async () => {
      try {
        // @api GET /api/modelli-configurazione/:id -> {"success":true,"data":{"id":"number","societaId":"number","codice":"string","nome":"string","articoloPadreId":"number","descrizioneTemplate":"string","componenti":[{"id":"number","ordine":"number","tipologia":"string","categoriaMerceologicaId":"number?","categoriaNome":"string","quantita":"number?","isPrincipale":"boolean","isInDescrizione":"boolean"}]}}
        const r = await apiFetch<{ success: boolean; data: ModelloDettaglio }>(
          `/api/modelli-configurazione/${modelloIdSel}`,
        );
        if (alive && r.success) setModelloDett(r.data);
      } catch {
        if (alive) setModelloDett(null);
      }
    };
    carica();
    return () => {
      alive = false;
    };
  }, [sorgenteTipo, modelloIdSel]);

  // ── Carica dettaglio preconfigurato quando cambia preconfIdSel ───────────
  useEffect(() => {
    let alive = true;
    if (sorgenteTipo !== 'PRECONFIGURATO' || !preconfIdSel) {
      setPreconfDett(null);
      return;
    }
    const carica = async () => {
      try {
        // @api GET /api/articoli-preconfigurati/:id -> {"success":true,"data":{"id":"number","societaId":"number","codice":"string","nome":"string","articoloPadreId":"number","componenti":[{"id":"number","ordine":"number","tipologia":"string","categoriaMerceologicaId":"number?","categoriaNome":"string","articoloId":"number?","articoloCodice":"string","articoloDescrizione":"string","articoloUmVenditaSimbolo":"string","quantita":"number","sfrido":"string|number|null","isPrincipale":"boolean","isInDescrizione":"boolean"}]}}
        const r = await apiFetch<{ success: boolean; data: PreconfDettaglio }>(
          `/api/articoli-preconfigurati/${preconfIdSel}`,
        );
        if (alive && r.success) setPreconfDett(r.data);
      } catch {
        if (alive) setPreconfDett(null);
      }
    };
    carica();
    return () => {
      alive = false;
    };
  }, [sorgenteTipo, preconfIdSel]);

  // ── Componi etichette+slotOrdine per la utility descrizione ─────────────
  const componentiPerDescrizione: ComponenteDescrizione[] = useMemo(() => {
    return figli.map((f) => ({
      slotOrdine: f.slotOrdine,
      descrizione: f.articolo
        ? f.articolo.descrizioneBase
        : f.slotEtichetta
          ? `[${f.slotEtichetta}]`
          : '',
      isInDescrizione: f.isInDescrizione,
    }));
  }, [figli]);

  // ── Helpers mutator ──────────────────────────────────────────────────────
  const aggiornaFigliaPatch = (key: string, patch: Partial<FigliaForm>) => {
    setFigli((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  };

  const aggiungiFiglia = () => {
    setFigli((prev) => [...prev, emptyFiglia()]);
  };

  const rimuoviFiglia = (key: string) => {
    setFigli((prev) => prev.filter((f) => f.key !== key));
  };

  const selezionaArticolo = (key: string, a: ArticoloLite) => {
    setFigli((prev) =>
      prev.map((f) => {
        if (f.key !== key) return f;
        const prezzoListino = a.prezzoListinoVendita ?? '';
        return {
          ...f,
          articolo: a,
          prezzoListino: f.prezzoListino || prezzoListino,
          prezzoVenditaUnitario: f.prezzoVenditaUnitario || prezzoListino,
          search: '',
          searchOpen: false,
        };
      }),
    );
  };

  // ── Ricomposizione da sorgente (modello / preconf / libera) ─────────────
  const ricomponiDaModello = (dett: ModelloDettaglio) => {
    const nuoviFigli: FigliaForm[] = dett.componenti.map((slot) => ({
      ...emptyFiglia(),
      quantita: slot.quantita != null ? String(slot.quantita) : '',
      isPrincipale: slot.isPrincipale,
      isInDescrizione: slot.isInDescrizione,
      componenteModelloId: slot.id,
      slotEtichetta: slot.categoriaNome || slot.tipologia.replace(/_/g, ' '),
      slotOrdine: slot.ordine,
    }));
    setFigli(nuoviFigli);
  };

  const ricomponiDaPreconf = (dett: PreconfDettaglio) => {
    const nuoviFigli: FigliaForm[] = dett.componenti.map((c) => {
      const art: ArticoloLite | null = c.articoloId
        ? {
            id: c.articoloId,
            codice: c.articoloCodice,
            descrizioneBase: c.articoloDescrizione,
            tipologia: c.tipologia,
            umVendita: { simbolo: c.articoloUmVenditaSimbolo || null },
          }
        : null;
      return {
        ...emptyFiglia(),
        articolo: art,
        quantita: String(c.quantita ?? ''),
        sfrido: c.sfrido != null ? String(c.sfrido) : '',
        isPrincipale: c.isPrincipale,
        isInDescrizione: c.isInDescrizione,
        componentePrecConfiguratoId: c.id,
        slotEtichetta: c.categoriaNome || c.tipologia.replace(/_/g, ' '),
        slotOrdine: null,
      };
    });
    setFigli(nuoviFigli);
  };

  // ── Cambio sorgente: con conferma se ci sono già figli compilati ────────
  const confermaCambioSorgente = (nuovaSorgente: SorgenteTipo): boolean => {
    const giaSporco = figli.length > 0 && figli.some((f) => f.articolo || f.quantita || f.prezzoVenditaUnitario);
    if (giaSporco) {
      return window.confirm(
        'Cambiando sorgente la composizione attuale verrà sostituita. Procedere?',
      );
    }
    return true;
  };

  const cambiaSorgenteTipo = (s: SorgenteTipo) => {
    if (s === sorgenteTipo) return;
    if (!confermaCambioSorgente(s)) return;
    setSorgenteTipo(s);
    if (s !== 'MODELLO') setModelloIdSel(null);
    if (s !== 'PRECONFIGURATO') setPreconfIdSel(null);
    if (s === 'LIBERA') {
      setFigli([emptyFiglia()]);
    } else {
      // MODELLO / PRECONFIGURATO: ricomposizione avverrà alla scelta dell'item
      setFigli([]);
    }
  };

  const cambiaModello = (nuovoId: number) => {
    if (nuovoId === modelloIdSel) return;
    if (!confermaCambioSorgente('MODELLO')) return;
    setModelloIdSel(nuovoId);
  };

  const cambiaPreconf = (nuovoId: number) => {
    if (nuovoId === preconfIdSel) return;
    if (!confermaCambioSorgente('PRECONFIGURATO')) return;
    setPreconfIdSel(nuovoId);
  };

  // Alla ricezione del dettaglio modello: ricomponi se figli vuoti o non
  // configurata in precedenza (cioè nessuna mappatura legittima da preservare)
  useEffect(() => {
    if (sorgenteTipo === 'MODELLO' && modelloDett) {
      // Ricomponi solo se: figli vuoti OR la sorgente è cambiata rispetto alla riga originale OR è una scelta esplicita (modelloIdSel diverso dall'iniziale)
      const figliVuoti = figli.length === 0;
      const idIniziale = riga.modelloConfigurazioneId;
      const cambiata = modelloDett.id !== idIniziale;
      if (figliVuoti || cambiata) {
        ricomponiDaModello(modelloDett);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelloDett]);

  useEffect(() => {
    if (sorgenteTipo === 'PRECONFIGURATO' && preconfDett) {
      const figliVuoti = figli.length === 0;
      const idIniziale = riga.articoloPrecConfiguratoId;
      const cambiata = preconfDett.id !== idIniziale;
      if (figliVuoti || cambiata) {
        ricomponiDaPreconf(preconfDett);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preconfDett]);

  // ── Inizializzazione descrizione: solo se non già valorizzata ───────────
  const descrizioneToccataRef = useRef<boolean>(!!riga.descrizioneEstesa);
  useEffect(() => {
    if (descrizioneToccataRef.current) return; // l'utente l'ha modificata o esiste già: non sovrascrivere
    if (figli.length === 0) return;
    const proposta = proponiDescrizione(
      sorgenteTipo,
      modelloDett?.descrizioneTemplate,
      componentiPerDescrizione,
    );
    if (proposta) {
      setDescrizioneEstesa(proposta);
      descrizioneToccataRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figli.length, modelloDett]);

  const rigeneraDescrizione = () => {
    const proposta = proponiDescrizione(
      sorgenteTipo,
      modelloDett?.descrizioneTemplate,
      componentiPerDescrizione,
    );
    setDescrizioneEstesa(proposta);
    descrizioneToccataRef.current = true;
  };

  // ── Calcoli quadratura live (centesimi) ─────────────────────────────────
  const centForfait = useMemo(() => toCents(forfait || 0), [forfait]);

  const { centSomma, centCosto, righeInvalide } = useMemo(() => {
    let somma = 0;
    let costo = 0;
    let invalide = 0;
    for (const f of figli) {
      const q = Number(f.quantita);
      const p = Number(f.prezzoVenditaUnitario);
      const c = f.costoUnitario === '' ? null : Number(f.costoUnitario);
      const qOk = Number.isFinite(q) && q > 0;
      const pOk = Number.isFinite(p) && p >= 0;
      const cOk = c === null || (Number.isFinite(c) && c >= 0);
      if (!f.articolo || !qOk || !pOk || !cOk) {
        invalide += 1;
        continue;
      }
      somma += toCents(q * p);
      if (c !== null) costo += toCents(q * c);
    }
    return { centSomma: somma, centCosto: costo, righeInvalide: invalide };
  }, [figli]);

  const quadra = centSomma === centForfait;
  const differenza = (centSomma - centForfait) / 100;
  const margine = (centForfait - centCosto) / 100;

  // Modello scelto se sorgente MODELLO, preconf se PRECONFIGURATO
  const sorgenteCompleta =
    (sorgenteTipo === 'MODELLO' && !!modelloIdSel) ||
    (sorgenteTipo === 'PRECONFIGURATO' && !!preconfIdSel) ||
    sorgenteTipo === 'LIBERA';
  // La quadratura col forfait è informativa, NON bloccante: si può confermare
  // anche se la somma dei componenti differisce dal forfait della riga.
  const confermaAbilitata =
    righeInvalide === 0 && figli.length > 0 && sorgenteCompleta && !salvando;

  // ── Submit ───────────────────────────────────────────────────────────────
  const salva = async () => {
    if (!confermaAbilitata) return;
    setSalvando(true);
    setErroreServer(null);
    setDettaglioErrore(null);
    try {
      const sorgente: Record<string, unknown> = { tipo: sorgenteTipo };
      if (sorgenteTipo === 'MODELLO') sorgente.modelloConfigurazioneId = modelloIdSel;
      if (sorgenteTipo === 'PRECONFIGURATO') sorgente.articoloPrecConfiguratoId = preconfIdSel;

      const body = {
        sorgente,
        figli: figli.map((f) => ({
          articoloId: f.articolo!.id,
          quantita: Number(f.quantita),
          prezzoVenditaUnitario: Number(f.prezzoVenditaUnitario),
          costoUnitario: f.costoUnitario === '' ? undefined : Number(f.costoUnitario),
          sfrido: f.sfrido === '' ? undefined : Number(f.sfrido),
          scontoAcquisto: f.scontoAcquisto === '' ? undefined : Number(f.scontoAcquisto),
          scontoVendita: f.scontoVendita === '' ? undefined : Number(f.scontoVendita),
          prezzoListino: f.prezzoListino === '' ? undefined : Number(f.prezzoListino),
          isPrincipale: f.isPrincipale,
          isInDescrizione: f.isInDescrizione,
          componenteModelloId: f.componenteModelloId ?? undefined,
          componentePrecConfiguratoId: f.componentePrecConfiguratoId ?? undefined,
        })),
        descrizioneEstesa,
      };

      // @api PUT /api/ordini/:id/righe/:rigaId/configurazione {"sorgente":{"tipo":"MODELLO|PRECONFIGURATO|LIBERA","modelloConfigurazioneId":"number?","articoloPrecConfiguratoId":"number?"},"figli":[{"articoloId":"number","quantita":"number","prezzoVenditaUnitario":"number","costoUnitario":"number?","sfrido":"number?","scontoAcquisto":"number?","scontoVendita":"number?","prezzoListino":"number?","isPrincipale":"boolean?","isInDescrizione":"boolean?","componenteModelloId":"number?","componentePrecConfiguratoId":"number?"}],"descrizioneEstesa":"string"} -> {"success":true,"data":{"rigaId":"number","nComponenti":"number","descrizioneEstesa":"string","margine":"number","forfait":"number","sommaComponenti":"number","differenza":"number"}}
      await apiFetch<{
        success: boolean;
        data?: {
          rigaId: number;
          nComponenti: number;
          descrizioneEstesa: string;
          margine: number;
          forfait: number;
          sommaComponenti: number;
          differenza: number;
        };
      }>(`/api/ordini/${ordineId}/righe/${riga.id}/configurazione`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      showToast('Riga configurata correttamente', 'success');
      onSaved();
    } catch (e) {
      const err = e as {
        message?: string;
        status?: number;
        payload?: {
          error?:
            | string
            | { code?: string; message?: string; forfait?: number; sommaFigli?: number; differenza?: number };
        };
      };
      const apiErr = err?.payload?.error;
      if (apiErr && typeof apiErr === 'object') {
        setErroreServer(apiErr.message || 'Errore configurazione');
        if (apiErr.code === 'QUADRATURA_NON_QUADRA') {
          setDettaglioErrore({
            forfait: apiErr.forfait,
            sommaFigli: apiErr.sommaFigli,
            differenza: apiErr.differenza,
          });
        }
      } else if (typeof apiErr === 'string') {
        setErroreServer(apiErr);
      } else {
        setErroreServer(err?.message || 'Errore configurazione');
      }
    } finally {
      setSalvando(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const articoloPadreLabel = riga.articolo
    ? `${riga.articolo.codice} — ${riga.articolo.descrizioneBase}`
    : riga.codicePrecopy
      ? `${riga.codicePrecopy} — ${riga.descrizione}`
      : riga.descrizione;

  return (
    <div className="bop-modal-config-ordine__overlay">
      <div className="bop-modal-config-ordine__box">
        <div className="bop-modal-config-ordine__header">
          <h2 className="bop-modal-config-ordine__title">
            {giaConfigurata ? 'Modifica configurazione riga' : 'Configura riga'}
          </h2>
          <button
            type="button"
            className="bop-btn-icon bop-modal-config-ordine__close"
            onClick={onClose}
            aria-label="Chiudi"
            title="Chiudi"
            disabled={salvando}
          >
            ×
          </button>
        </div>

        <div className="bop-modal-config-ordine__body">
          {/* Header read-only (D76) */}
          <div className="bop-modal-config-ordine__padre">
            <div className="bop-modal-config-ordine__padre-row">
              <span className="bop-text-muted">Articolo padre</span>
              <strong>{articoloPadreLabel}</strong>
            </div>
            <div className="bop-modal-config-ordine__padre-row">
              <span className="bop-text-muted">Quantità</span>
              <strong>
                {formatNumber(Number(riga.quantita ?? 0))} {riga.umVendita ?? ''}
              </strong>
            </div>
            <div className="bop-modal-config-ordine__padre-row">
              <span className="bop-text-muted">Prezzo unitario</span>
              <strong>{formatCurrency(Number(riga.prezzoUnitario ?? 0))}</strong>
            </div>
            <div className="bop-modal-config-ordine__padre-row bop-modal-config-ordine__padre-row--forfait">
              <span>Forfait target</span>
              <strong>{formatCurrency(forfait)}</strong>
            </div>
          </div>

          {/* Selettore sorgente (D66/D67/D75) */}
          <div className="bop-modal-config-ordine__sorgente">
            <div className="bop-label">Sorgente di partenza</div>
            <div className="bop-modal-config-ordine__sorgente-tabs">
              {(['MODELLO', 'PRECONFIGURATO', 'LIBERA'] as SorgenteTipo[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={
                    'bop-modal-config-ordine__sorgente-tab' +
                    (sorgenteTipo === s
                      ? ' bop-modal-config-ordine__sorgente-tab--active'
                      : '')
                  }
                  onClick={() => cambiaSorgenteTipo(s)}
                  disabled={salvando}
                >
                  {s === 'MODELLO'
                    ? 'Modello'
                    : s === 'PRECONFIGURATO'
                      ? 'Preconfigurato'
                      : 'Libera'}
                </button>
              ))}
            </div>

            {sorgenteTipo === 'MODELLO' && (
              <div className="bop-modal-config-ordine__sorgente-picker">
                <label className="bop-label">Modello di configurazione</label>
                <select
                  className="bop-select"
                  value={modelloIdSel ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) cambiaModello(parseInt(v, 10));
                  }}
                  disabled={salvando || loadingLookup}
                >
                  <option value="">— Seleziona un modello —</option>
                  {modelliLista.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.codice} — {m.nome} ({m.articoloPadreCodice})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {sorgenteTipo === 'PRECONFIGURATO' && (
              <div className="bop-modal-config-ordine__sorgente-picker">
                <label className="bop-label">Articolo pre-configurato</label>
                <select
                  className="bop-select"
                  value={preconfIdSel ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) cambiaPreconf(parseInt(v, 10));
                  }}
                  disabled={salvando || loadingLookup}
                >
                  <option value="">— Seleziona un pre-configurato —</option>
                  {preconfLista.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.codice} — {p.nome} ({p.articoloPadreCodice})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Tabella figli ricchi (D68/D69) */}
          <table className="bop-table bop-modal-config-ordine__tabella">
            <thead>
              <tr>
                <th>Componente</th>
                <th className="bop-text-right">Q.tà</th>
                <th>U.M.</th>
                <th className="bop-text-right">Prezzo list.</th>
                <th className="bop-text-right">Sc. acq. %</th>
                <th className="bop-text-right">Sc. vend. %</th>
                <th className="bop-text-right">Prezzo vend.</th>
                <th className="bop-text-right">Costo unit.</th>
                <th className="bop-text-right">Sfrido</th>
                <th>In descr.</th>
                <th className="bop-text-right">Subtotale</th>
                <th className="bop-modal-config-ordine__col-rm"> </th>
              </tr>
            </thead>
            <tbody>
              {figli.length === 0 && (
                <tr>
                  <td colSpan={12}>
                    <div className="bop-empty-state">
                      {sorgenteTipo === 'LIBERA'
                        ? 'Aggiungi i componenti.'
                        : sorgenteTipo === 'MODELLO'
                          ? 'Seleziona un modello per pre-popolare i componenti.'
                          : 'Seleziona un pre-configurato per pre-popolare i componenti.'}
                    </div>
                  </td>
                </tr>
              )}
              {figli.map((f) => (
                <RigaFigliaConfigEditabile
                  key={f.key}
                  figlia={f}
                  societaId={societaId}
                  onPatch={(patch) => aggiornaFigliaPatch(f.key, patch)}
                  onSeleziona={(a) => selezionaArticolo(f.key, a)}
                  onRimuovi={() => rimuoviFiglia(f.key)}
                  disabilita={salvando}
                />
              ))}
            </tbody>
          </table>

          <div className="bop-modal-config-ordine__azioni-figli">
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={aggiungiFiglia}
              disabled={salvando}
            >
              + Aggiungi componente
            </button>
          </div>

          {/* Quadratura live (D65/D70) */}
          <div
            className={
              'bop-modal-config-ordine__quadratura' +
              (quadra
                ? ' bop-modal-config-ordine__quadratura--ok'
                : ' bop-modal-config-ordine__quadratura--ko')
            }
          >
            <div className="bop-modal-config-ordine__quadratura-row">
              <span>Somma componenti (vendita)</span>
              <strong>{formatCurrency(centSomma / 100)}</strong>
            </div>
            <div className="bop-modal-config-ordine__quadratura-row">
              <span>Forfait</span>
              <strong>{formatCurrency(centForfait / 100)}</strong>
            </div>
            <div className="bop-modal-config-ordine__quadratura-row bop-modal-config-ordine__quadratura-row--delta">
              <span>{quadra ? 'Quadratura' : 'Differenza'}</span>
              <strong>
                {quadra ? 'OK' : `${differenza > 0 ? '+' : ''}${formatCurrency(differenza)}`}
              </strong>
            </div>
            <div className="bop-modal-config-ordine__quadratura-row bop-modal-config-ordine__quadratura-row--margine">
              <span>Margine (forfait − costi)</span>
              <strong>{formatCurrency(margine)}</strong>
            </div>
            {righeInvalide > 0 && (
              <div className="bop-modal-config-ordine__quadratura-row bop-text-danger">
                <span>Righe incomplete</span>
                <strong>{formatNumber(righeInvalide)}</strong>
              </div>
            )}
          </div>

          {/* Descrizione (D72/D73 + D80/D81) */}
          <div className="bop-modal-config-ordine__descrizione">
            <div className="bop-modal-config-ordine__descrizione-header">
              <label className="bop-label">Descrizione estesa (per fattura a forfait)</label>
              <button
                type="button"
                className="bop-btn-secondary bop-btn-sm"
                onClick={rigeneraDescrizione}
                disabled={salvando || figli.length === 0}
                title="Rigenera dalla composizione attuale"
              >
                Rigenera descrizione dalla composizione
              </button>
            </div>
            <textarea
              className="bop-input bop-modal-config-ordine__descrizione-textarea"
              value={descrizioneEstesa}
              onChange={(e) => {
                setDescrizioneEstesa(e.target.value);
                descrizioneToccataRef.current = true;
              }}
              disabled={salvando}
              rows={5}
            />
          </div>

          {erroreServer && (
            <div className="bop-modal-config-ordine__errore">
              <strong>Errore:</strong> {erroreServer}
              {dettaglioErrore && (
                <div className="bop-modal-config-ordine__errore-dettaglio">
                  Forfait {formatCurrency(dettaglioErrore.forfait ?? 0)} · Somma{' '}
                  {formatCurrency(dettaglioErrore.sommaFigli ?? 0)} · Differenza{' '}
                  {formatCurrency(dettaglioErrore.differenza ?? 0)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bop-modal-config-ordine__footer">
          <button
            type="button"
            className="bop-btn-secondary"
            onClick={onClose}
            disabled={salvando}
          >
            Annulla
          </button>
          <button
            type="button"
            className="bop-btn-primary"
            onClick={salva}
            disabled={!confermaAbilitata}
            title={
              !sorgenteCompleta
                ? 'Seleziona la sorgente di partenza'
                : figli.length === 0
                  ? 'Aggiungi almeno un componente'
                  : righeInvalide > 0
                    ? 'Completa o rimuovi i componenti incompleti'
                    : 'Conferma configurazione'
            }
          >
            {salvando ? 'Salvataggio…' : 'Conferma configurazione'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Riga figlia editabile + autocomplete articolo ─────────────────────────
interface RigaFigliaProps {
  figlia: FigliaForm;
  societaId: number;
  onPatch: (patch: Partial<FigliaForm>) => void;
  onSeleziona: (a: ArticoloLite) => void;
  onRimuovi: () => void;
  disabilita: boolean;
}

const RigaFigliaConfigEditabile: React.FC<RigaFigliaProps> = ({
  figlia,
  societaId,
  onPatch,
  onSeleziona,
  onRimuovi,
  disabilita,
}) => {
  const [results, setResults] = useState<ArticoloLite[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const subtotale = useMemo(() => {
    const q = Number(figlia.quantita);
    const p = Number(figlia.prezzoVenditaUnitario);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return null;
    return q * p;
  }, [figlia.quantita, figlia.prezzoVenditaUnitario]);

  useEffect(() => {
    if (!figlia.searchOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          societaId: String(societaId),
          attivo: 'true',
          limit: '50',
        });
        if (figlia.search.trim()) params.set('q', figlia.search.trim());
        // @api GET /api/anagrafiche/articoli?societaId&q&attivo&limit -> {"success":true,"data":[{"id":"number","codice":"string","descrizioneBase":"string","tipologia":"string","umVendita":{"simbolo":"string"},"prezzoListinoVendita":"string|null"}],"total":"number"}
        const res = await apiFetch<{ success: boolean; data: ArticoloLite[]; total?: number }>(
          `/api/anagrafiche/articoli?${params.toString()}`,
        );
        setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [figlia.search, figlia.searchOpen, societaId]);

  const umLabel = figlia.articolo?.umVendita?.simbolo ?? '—';

  return (
    <tr className="bop-modal-config-ordine__tr">
      <td className="bop-modal-config-ordine__cell-articolo">
        {figlia.slotEtichetta && !figlia.articolo && (
          <div className="bop-modal-config-ordine__slot-etichetta">
            <span className="bop-badge bop-badge--neutral">{figlia.slotEtichetta}</span>
          </div>
        )}
        {figlia.articolo ? (
          <div className="bop-modal-config-ordine__articolo-sel">
            <div className="bop-modal-config-ordine__articolo-line1">
              <span className="bop-modal-config-ordine__articolo-codice">
                {figlia.articolo.codice}
              </span>
              {figlia.slotEtichetta && (
                <span className="bop-badge bop-badge--neutral">{figlia.slotEtichetta}</span>
              )}
            </div>
            <div className="bop-modal-config-ordine__articolo-desc">
              {figlia.articolo.descrizioneBase}
            </div>
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={() => onPatch({ searchOpen: true, search: '' })}
              disabled={disabilita}
            >
              Cambia articolo
            </button>
          </div>
        ) : (
          <input
            type="text"
            className="bop-input"
            placeholder="Cerca articolo per codice o descrizione…"
            value={figlia.search}
            onChange={(e) => onPatch({ search: e.target.value, searchOpen: true })}
            onFocus={() => onPatch({ searchOpen: true })}
            disabled={disabilita}
          />
        )}

        {figlia.searchOpen && (
          <div className="bop-modal-config-ordine__autocomplete">
            <div className="bop-modal-config-ordine__autocomplete-header">
              {figlia.articolo && (
                <input
                  type="text"
                  className="bop-input bop-modal-config-ordine__autocomplete-search"
                  placeholder="Cerca articolo per codice o descrizione…"
                  value={figlia.search}
                  onChange={(e) => onPatch({ search: e.target.value })}
                  disabled={disabilita}
                  autoFocus
                />
              )}
              <button
                type="button"
                className="bop-btn-secondary bop-btn-sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPatch({ searchOpen: false, search: '' });
                }}
              >
                Annulla
              </button>
            </div>
            {loading && (
              <div className="bop-modal-config-ordine__autocomplete-empty">
                Ricerca in corso…
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="bop-modal-config-ordine__autocomplete-empty">
                Nessun articolo trovato
              </div>
            )}
            {!loading &&
              results.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="bop-modal-config-ordine__autocomplete-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSeleziona(a);
                  }}
                >
                  <span className="bop-modal-config-ordine__articolo-codice">{a.codice}</span>
                  <span className="bop-badge bop-badge--neutral">
                    {a.tipologia.replace(/_/g, ' ')}
                  </span>
                  <span className="bop-modal-config-ordine__articolo-desc">
                    {a.descrizioneBase}
                  </span>
                  {a.umVendita?.simbolo && (
                    <span className="bop-text-muted">u.m. {a.umVendita.simbolo}</span>
                  )}
                  {a.prezzoListinoVendita && (
                    <span className="bop-modal-config-ordine__autocomplete-price">
                      {formatCurrency(parseFloat(a.prezzoListinoVendita))}
                    </span>
                  )}
                </button>
              ))}
          </div>
        )}
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.quantita}
          onChange={(e) => onPatch({ quantita: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td>{umLabel}</td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.prezzoListino}
          onChange={(e) => onPatch({ prezzoListino: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.scontoAcquisto}
          onChange={(e) => onPatch({ scontoAcquisto: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.scontoVendita}
          onChange={(e) => onPatch({ scontoVendita: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.prezzoVenditaUnitario}
          onChange={(e) => onPatch({ prezzoVenditaUnitario: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.costoUnitario}
          onChange={(e) => onPatch({ costoUnitario: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        <input
          type="number"
          step="0.01"
          min="0"
          className="bop-input bop-modal-config-ordine__input-num"
          value={figlia.sfrido}
          onChange={(e) => onPatch({ sfrido: e.target.value })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-center">
        <input
          type="checkbox"
          checked={figlia.isInDescrizione}
          onChange={(e) => onPatch({ isInDescrizione: e.target.checked })}
          disabled={disabilita}
        />
      </td>
      <td className="bop-text-right">
        {subtotale != null ? formatCurrency(subtotale) : '—'}
      </td>
      <td className="bop-modal-config-ordine__col-rm">
        <button
          type="button"
          className="bop-btn-icon bop-modal-config-ordine__btn-rm"
          onClick={onRimuovi}
          disabled={disabilita}
          aria-label="Rimuovi componente"
          title="Rimuovi componente"
        >
          ×
        </button>
      </td>
    </tr>
  );
};

export default ModalConfigurazioneRigaOrdine;
